import { Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { NotificationProvider } from '@/context/NotificationContext';
import { ProtectedRoute, PublicRoute } from '@/components/ProtectedRoute';
import { MainLayout } from '@/components/Layout/MainLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { NotificationContainer } from '@/components/ui/NotificationContainer';
import { SessionWarning } from '@/components/auth/SessionWarning';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// Authentication pages (keep these loaded for faster initial login)
import { Login } from '@/pages/auth/Login';
import { Register } from '@/pages/auth/Register';
import { PasswordReset } from '@/pages/auth/PasswordReset';
import { PendingApproval } from '@/pages/auth/PendingApproval';

// Lazy load main application pages
const Dashboard = lazy(() =>
  import('@/pages/Dashboard').then(m => ({ default: m.Dashboard }))
);
const Organizations = lazy(() => import('@/pages/Organizations'));
const OrganizationDetail = lazy(() =>
  import('@/pages/OrganizationDetail').then(m => ({
    default: m.OrganizationDetail,
  }))
);
const UnifiedConfiguration = lazy(() =>
  import('@/pages/UnifiedConfiguration').then(m => ({
    default: m.UnifiedConfiguration,
  }))
);
const Calculations = lazy(() =>
  import('@/pages/Calculations').then(m => ({ default: m.Calculations }))
);
const CalculationReport = lazy(() =>
  import('@/pages/CalculationReport').then(m => ({
    default: m.CalculationReport,
  }))
);
const Reports = lazy(() =>
  import('@/pages/Reports').then(m => ({ default: m.Reports }))
);
const ReportGenerationPage = lazy(() =>
  import('@/pages/ReportGeneration').then(m => ({
    default: m.ReportGenerationPage,
  }))
);
const HistoricalComparisonPage = lazy(() =>
  import('@/pages/HistoricalComparison').then(m => ({
    default: m.HistoricalComparisonPage,
  }))
);
const DataManagement = lazy(() =>
  import('@/pages/DataManagement').then(m => ({ default: m.DataManagement }))
);
const Users = lazy(() =>
  import('@/pages/Users').then(m => ({ default: m.Users }))
);
const Profile = lazy(() =>
  import('@/pages/Profile').then(m => ({ default: m.Profile }))
);

function App() {
  return (
    <ErrorBoundary showDetails={import.meta.env.DEV}>
      <Router>
        <AuthProvider>
          <NotificationProvider>
            <Routes>
              {/* Public routes (redirect to dashboard if authenticated) */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <Register />
                  </PublicRoute>
                }
              />
              <Route
                path="/password-reset"
                element={
                  <PublicRoute>
                    <PasswordReset />
                  </PublicRoute>
                }
              />

              {/* Pending approval route (requires authentication but not approval) */}
              <Route
                path="/pending-approval"
                element={
                  <ProtectedRoute requireApproval={false}>
                    <PendingApproval />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes (require authentication and approval) */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route
                  path="dashboard"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <Dashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="organizations"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <Organizations />
                    </Suspense>
                  }
                />
                <Route
                  path="organizations/:id"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <OrganizationDetail />
                    </Suspense>
                  }
                />
                <Route
                  path="organizations/:id/configuration"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <UnifiedConfiguration />
                    </Suspense>
                  }
                />
                <Route
                  path="calculations"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <Calculations />
                    </Suspense>
                  }
                />
                <Route
                  path="calculations/:id"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <CalculationReport />
                    </Suspense>
                  }
                />
                <Route
                  path="reports"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <Reports />
                    </Suspense>
                  }
                />
                <Route
                  path="reports/generate"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <ReportGenerationPage />
                    </Suspense>
                  }
                />
                <Route
                  path="analysis"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <HistoricalComparisonPage />
                    </Suspense>
                  }
                />
                <Route
                  path="configuration"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <UnifiedConfiguration />
                    </Suspense>
                  }
                />
                <Route
                  path="data-management"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <DataManagement />
                    </Suspense>
                  }
                />
                <Route
                  path="users"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <Users />
                    </Suspense>
                  }
                />
                <Route
                  path="profile"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <Profile />
                    </Suspense>
                  }
                />
              </Route>

              {/* Fallback route */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            <NotificationContainer />
            <SessionWarning />
          </NotificationProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
